/* AgentMeeting — Studio (Agent Workshop) */

import React from 'react';
import { AGENTS, MODELS, ModelGlyph, STANCE_COLOR } from '../shared/data';
import { Avatar, I, ModelChip, StanceTag, renderInline, useStreamedText } from '../shared/ui';
import { agents as agentsApi, knowledge as knowledgeApi, expertLibrary, HTTPError, type AgentRecord, type AgentTemplate } from '../shared/api';
import { useAuth } from '../shared/auth';
import { useI18n } from '../shared/i18n';
// SegRadio + Toggle are defined locally in this file — Wave 3 will refactor to use shared variants

// ─── Helpers: map between backend (provider+model) and local ModelId ─────
type LocalModelId = keyof typeof MODELS;

function findLocalModelId(provider?: string, model?: string): LocalModelId | null {
  if (!provider || !model) return null;
  const fqn = `${provider}/${model}`;
  for (const id of Object.keys(MODELS) as LocalModelId[]) {
    if (MODELS[id].name === fqn) return id;
  }
  return null;
}

function splitModelFqn(localId: LocalModelId): { provider: string; model: string } {
  const fqn = MODELS[localId].name; // e.g. "openai/gpt-4o"
  const slash = fqn.indexOf('/');
  if (slash < 0) return { provider: localId, model: fqn };
  return { provider: fqn.slice(0, slash), model: fqn.slice(slash + 1) };
}

interface UIAgent {
  id: string;            // backend uuid, or 'new-<n>' for unsaved
  name: string;
  persona: string;
  stance: string;
  stance_label: string;
  short: string;
  modelLocal: LocalModelId | null;
  isNew?: boolean;
}

function recordToUI(rec: AgentRecord): UIAgent {
  return {
    id: rec.id,
    name: rec.name,
    persona: rec.persona,
    stance: rec.stance,
    stance_label: rec.stance_label,
    short: rec.short,
    modelLocal: findLocalModelId(rec.provider, rec.model),
  };
}

function blankAgent(seq: number): UIAgent {
  return {
    id: 'new-' + seq,
    name: 'New agent',
    persona: '',
    stance: 'neutral',
    stance_label: '中立',
    short: 'NA',
    modelLocal: 'claude-sonnet',
    isNew: true,
  };
}

// Pseudo AgentT shim so existing UI components (Avatar, StanceTag, ModelChip) can render.
function uiToShim(a: UIAgent): any {
  return {
    id: a.id,
    name: a.name,
    short: a.short,
    stance: a.stance,
    stanceLabel: a.stance_label,
    model: a.modelLocal,
    persona: a.persona,
  };
}

const STANCE_OPTS = [
  { id: 'pro',     label: '推进',     color: 'var(--pro)',     stanceLabel: '推进派' },
  { id: 'con',     label: '反对',     color: 'var(--err)',     stanceLabel: '反对派' },
  { id: 'neutral', label: '中立',     color: 'var(--neutral)', stanceLabel: '中立' },
  { id: 'finance', label: '财务',     color: 'var(--finance)', stanceLabel: '财务派' },
  { id: 'risk',    label: '风险',     color: 'var(--warn)',    stanceLabel: '风险派' },
  { id: 'judge',   label: '评委',     color: 'var(--judge)',   stanceLabel: '评委' },
];

export function Studio({ embedded, topBar }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const [serverAgents, setServerAgents] = React.useState<UIAgent[] | null>(null);
  const [agentsErr, setAgentsErr] = React.useState<string | null>(null);
  const [agentsLoading, setAgentsLoading] = React.useState(true);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [draftMap, setDraftMap] = React.useState<Record<string, UIAgent>>({});
  const [newSeq, setNewSeq] = React.useState(1);

  const [packs, setPacks] = React.useState([
    { id: 'p1', name: '高管会议', count: 5, agentIds: ['A1','A2','A3','A4','A5'] },
    { id: 'p2', name: '红蓝对抗', count: 4, agentIds: ['A1','A2','A3','A4'] },
    { id: 'p3', name: '苏格拉底', count: 3, agentIds: ['A1','A2','A6'] },
  ]);
  const [editingPack, setEditingPack] = React.useState(null);

  // Expert library drawer
  const [libraryOpen, setLibraryOpen] = React.useState(false);
  const [templates, setTemplates] = React.useState<AgentTemplate[] | null>(null);
  const [libraryCat, setLibraryCat] = React.useState<'professional' | 'celebrity'>('professional');
  const [libraryTag, setLibraryTag] = React.useState<string>('');
  const [instantiating, setInstantiating] = React.useState<string | null>(null);
  const [libraryErr, setLibraryErr] = React.useState<string | null>(null);

  const openLibrary = React.useCallback(async () => {
    setLibraryOpen(true);
    setLibraryErr(null);
    if (templates) return;
    try {
      const res = await expertLibrary.list();
      setTemplates(res.templates || []);
    } catch (e) {
      setLibraryErr(e instanceof HTTPError ? `HTTP ${e.status}` : `${e}`);
    }
  }, [templates]);

  const onInstantiate = async (slug: string) => {
    setInstantiating(slug);
    setLibraryErr(null);
    try {
      const res = await expertLibrary.instantiate(slug);
      // Refresh agents list and select the newly created one.
      await refreshAgents();
      setSelectedId(res.id);
      setLibraryOpen(false);
    } catch (e) {
      setLibraryErr(e instanceof HTTPError ? `HTTP ${e.status}: ${e.body?.error || ''}` : `${e}`);
    } finally {
      setInstantiating(null);
    }
  };

  const refreshAgents = React.useCallback(async () => {
    setAgentsLoading(true);
    setAgentsErr(null);
    try {
      const { agents: list } = await agentsApi.list();
      const ui = list.map(recordToUI);
      setServerAgents(ui);
      // If no selection yet, pick first.
      setSelectedId(prev => prev || (ui[0]?.id ?? null));
    } catch (e) {
      const msg = e instanceof HTTPError
        ? `agents.list failed (HTTP ${e.status})`
        : (e as Error).message || 'agents.list failed';
      setAgentsErr(msg);
      setServerAgents([]);
    } finally {
      setAgentsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (!user) return;
    refreshAgents();
  }, [user, refreshAgents]);

  // Combine server agents + unsaved drafts for the sidebar.
  const allAgents: UIAgent[] = React.useMemo(() => {
    const server = serverAgents || [];
    const drafts = Object.values(draftMap).filter(d => d.isNew);
    return [...server, ...drafts];
  }, [serverAgents, draftMap]);

  // Resolve current selection — prefer local draft (edited copy) if present.
  const selected: UIAgent | null = React.useMemo(() => {
    if (!selectedId) return null;
    if (draftMap[selectedId]) return draftMap[selectedId];
    return allAgents.find(a => a.id === selectedId) || null;
  }, [selectedId, draftMap, allAgents]);

  const updateDraft = (patch: Partial<UIAgent>) => {
    if (!selectedId || !selected) return;
    setDraftMap(m => ({ ...m, [selectedId]: { ...selected, ...patch } }));
  };

  const onCreateNew = () => {
    const draft = blankAgent(newSeq);
    setNewSeq(n => n + 1);
    setDraftMap(m => ({ ...m, [draft.id]: draft }));
    setSelectedId(draft.id);
  };

  const [savingAgent, setSavingAgent] = React.useState(false);
  const [saveErr, setSaveErr] = React.useState<string | null>(null);

  const onSaveAgent = async () => {
    if (!selected) return;
    setSavingAgent(true);
    setSaveErr(null);
    const local = selected.modelLocal || 'claude-sonnet';
    const { provider, model } = splitModelFqn(local);
    const body = {
      name: selected.name,
      persona: selected.persona,
      stance: selected.stance,
      stance_label: selected.stance_label,
      short: selected.short,
      provider,
      model,
    };
    try {
      if (selected.isNew || selected.id.startsWith('new-')) {
        const { id } = await agentsApi.create(body);
        // Drop draft, refresh, then select the freshly-created agent.
        setDraftMap(m => {
          const next = { ...m };
          delete next[selected.id];
          return next;
        });
        await refreshAgents();
        setSelectedId(id);
      } else {
        // Optimistic update: write into serverAgents immediately.
        setServerAgents(arr => (arr || []).map(a =>
          a.id === selected.id ? { ...a, ...selected, isNew: false } : a
        ));
        await agentsApi.update(selected.id, body);
        // Drop draft now that it's persisted.
        setDraftMap(m => {
          const next = { ...m };
          delete next[selected.id];
          return next;
        });
        await refreshAgents();
      }
    } catch (e) {
      const msg = e instanceof HTTPError
        ? `save failed (HTTP ${e.status})`
        : (e as Error).message || 'save failed';
      setSaveErr(msg);
      // On failure, revert by re-fetching authoritative state.
      await refreshAgents();
    } finally {
      setSavingAgent(false);
    }
  };

  const onDuplicate = async () => {
    if (!selected) return;
    setSavingAgent(true);
    setSaveErr(null);
    const local = selected.modelLocal || 'claude-sonnet';
    const { provider, model } = splitModelFqn(local);
    const body = {
      name: selected.name + ' (copy)',
      persona: selected.persona,
      stance: selected.stance,
      stance_label: selected.stance_label,
      short: selected.short,
      provider,
      model,
    };
    try {
      const { id } = await agentsApi.create(body);
      await refreshAgents();
      setSelectedId(id);
    } catch (e) {
      const msg = e instanceof HTTPError
        ? `duplicate failed (HTTP ${e.status})`
        : (e as Error).message || 'duplicate failed';
      setSaveErr(msg);
    } finally {
      setSavingAgent(false);
    }
  };

  const onDelete = async () => {
    if (!selected) return;
    // Unsaved drafts — drop locally.
    if (selected.isNew || selected.id.startsWith('new-')) {
      setDraftMap(m => {
        const next = { ...m };
        delete next[selected.id];
        return next;
      });
      const fallback = (serverAgents && serverAgents[0]?.id) || null;
      setSelectedId(fallback);
      return;
    }
    if (!window.confirm(`Delete "${selected.name}"? This cannot be undone.`)) return;
    setSavingAgent(true);
    setSaveErr(null);
    try {
      await agentsApi.remove(selected.id);
      // Optimistic: drop from list, pick another.
      const remaining = (serverAgents || []).filter(a => a.id !== selected.id);
      setServerAgents(remaining);
      setSelectedId(remaining[0]?.id || null);
      await refreshAgents();
    } catch (e) {
      const msg = e instanceof HTTPError
        ? `delete failed (HTTP ${e.status})`
        : (e as Error).message || 'delete failed';
      setSaveErr(msg);
      await refreshAgents();
    } finally {
      setSavingAgent(false);
    }
  };

  const onAddPack = () => setEditingPack('new');
  const onEditPack = (id) => setEditingPack(id);
  const onSavePack = (data) => {
    if (editingPack === 'new') {
      setPacks(p => [...p, { id: 'p' + Date.now(), ...data }]);
    } else {
      setPacks(p => p.map(x => x.id === editingPack ? { ...x, ...data } : x));
    }
    setEditingPack(null);
  };
  const onDeletePack = (id) => {
    setPacks(p => p.filter(x => x.id !== id));
    setEditingPack(null);
  };
  const editing = editingPack === 'new'
    ? { id: 'new', name: '', agentIds: [] }
    : packs.find(p => p.id === editingPack);

  return (
    <div className="frame" style={{ height: '100%' }}>
      {topBar}
      {!embedded && (<>
      <div className="term-bar">
        <div className="lights"><span/><span/><span/></div>
        <div className="crumbs">
          <span>~/agents</span>
          <span className="sep">/</span>
          <span className="here">studio</span>
        </div>
        <div className="right">
          <span className="mono tiny dim">
            {agentsLoading ? 'loading…' : `${allAgents.length} agents`}
          </span>
          <button className="btn" onClick={openLibrary} title="从专家库一键拉起">📚 专家库</button>
          <button className="btn" onClick={onCreateNew} title={t('app.studio.save')}>{I.plus} new agent</button>
        </div>
      </div>
      </>)}

      <div className="grow flex" style={{ minHeight: 0 }}>
        {/* left: agent list */}
        <div className="col" style={{ width: 260, flex: '0 0 260px', borderRight: '1px solid var(--line-1)', background: 'var(--bg-1)' }}>
          <div style={{ padding: '12px 14px 8px' }}>
            <div className="input flex center gap-2" style={{ padding: '6px 10px' }}>
              {I.search}<input style={{
                flex: 1, background: 'transparent', border: 0, outline: 'none', color: 'var(--fg-0)',
                fontFamily: 'var(--mono)', fontSize: 11,
              }} placeholder="filter agents…" />
              <span className="kbd">/</span>
            </div>
          </div>
          <div className="flex between center" style={{ padding: '8px 14px 4px' }}>
            <div className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>
              MY AGENTS · {allAgents.length}
            </div>
            <div className="flex gap-1">
              <button
                className="btn ghost"
                onClick={openLibrary}
                title="从专家库一键拉起 (30+ 专业 / 名人角色)"
                style={{ fontSize: 10, padding: '3px 8px' }}
              >📚 专家库</button>
              <button className="btn ghost icon" onClick={onCreateNew} title="new agent">{I.plus}</button>
            </div>
          </div>

          {agentsErr && (
            <div className="mono tiny" style={{
              margin: '4px 14px 8px', padding: '6px 8px',
              border: '1px solid var(--err)', borderRadius: 3, color: 'var(--err)',
              background: 'rgba(220,38,38,0.06)',
            }}>
              {agentsErr}
              <button className="btn ghost icon" style={{ marginLeft: 8 }}
                onClick={() => refreshAgents()} title="retry">{I.refresh || '↻'}</button>
            </div>
          )}

          <div className="grow scroll col">
            {agentsLoading && allAgents.length === 0 && (
              <div className="meta" style={{ padding: '14px' }}>loading agents…</div>
            )}
            {!agentsLoading && allAgents.length === 0 && !agentsErr && (
              <div className="meta" style={{ padding: '14px' }}>
                no agents yet · click + to create one
              </div>
            )}
            {allAgents.map(a => (
              <div key={a.id}
                onClick={() => setSelectedId(a.id)}
                className="flex center gap-2 hov"
                style={{
                  padding: '8px 14px', cursor: 'pointer',
                  background: selectedId === a.id ? 'var(--bg-3)' : 'transparent',
                  borderLeft: '2px solid ' + (selectedId === a.id ? 'var(--live)' : 'transparent'),
                }}
              >
                <Avatar agent={uiToShim(a)} size="sm" />
                <div className="col grow" style={{ minWidth: 0 }}>
                  <div className="sm" style={{ fontWeight: 500 }}>
                    {a.name}
                    {a.isNew && <span className="dim mono tiny" style={{ marginLeft: 6 }}>· unsaved</span>}
                    {draftMap[a.id] && !a.isNew && (
                      <span className="dim mono tiny" style={{ marginLeft: 6 }}>· edited</span>
                    )}
                  </div>
                  <div className="flex center gap-2">
                    {a.modelLocal && <ModelChip modelId={a.modelLocal} />}
                    <StanceTag agent={uiToShim(a)} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="hr" />
          <div className="col gap-2" style={{ padding: 14 }}>
            <div className="flex between center">
              <div className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>STARTER PACKS · {packs.length}</div>
              <button className="btn ghost icon" onClick={onAddPack} title="new pack">{I.plus}</button>
            </div>
            {packs.map(p => (
              <div key={p.id} className="hov flex center between" style={{
                padding: '6px 8px', borderRadius: 4, cursor: 'pointer',
                border: '1px solid var(--line-1)',
              }} onClick={() => onEditPack(p.id)}>
                <span className="sm">{p.name} · {p.agentIds.length} 人</span>
                <span className="mono tiny dim">edit</span>
              </div>
            ))}
            {packs.length === 0 && (
              <div className="meta" style={{ padding: '4px 0' }}>no packs yet · click + to add</div>
            )}
          </div>
        </div>

        {/* center: edit form */}
        <div className="col grow scroll" style={{ minWidth: 0 }}>
          {selected ? (
            <StudioForm
              agent={selected}
              onChange={updateDraft}
              onSave={onSaveAgent}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              saving={savingAgent}
              error={saveErr}
              dirty={!!draftMap[selected.id]}
            />
          ) : (
            <div className="meta" style={{ padding: 28 }}>
              {agentsLoading
                ? 'loading…'
                : 'No agent selected. Click + to create one.'}
            </div>
          )}
        </div>

        {/* right: live preview */}
        <div className="col" style={{ width: 360, flex: '0 0 360px', borderLeft: '1px solid var(--line-1)', background: 'var(--bg-1)' }}>
          <div className="flex between center" style={{ padding: '12px 14px', borderBottom: '1px solid var(--line-1)' }}>
            <div className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>LIVE PREVIEW · dry run</div>
            <button className="btn ghost icon">{I.dots}</button>
          </div>
          {selected
            ? <StudioPreview agent={selected} />
            : <div className="meta" style={{ padding: 14 }}>select an agent to preview</div>}
        </div>
      </div>

      {editingPack && (
        <PackEditor
          pack={editing}
          isNew={editingPack === 'new'}
          onSave={onSavePack}
          onDelete={() => onDeletePack(editingPack)}
          onCancel={() => setEditingPack(null)}
        />
      )}

      {libraryOpen && (
        <ExpertLibraryModal
          templates={templates}
          category={libraryCat}
          setCategory={setLibraryCat}
          tag={libraryTag}
          setTag={setLibraryTag}
          instantiating={instantiating}
          onInstantiate={onInstantiate}
          onClose={() => setLibraryOpen(false)}
          err={libraryErr}
        />
      )}
    </div>
  );
}

function ExpertLibraryModal({
  templates, category, setCategory, tag, setTag,
  instantiating, onInstantiate, onClose, err,
}: {
  templates: AgentTemplate[] | null;
  category: 'professional' | 'celebrity';
  setCategory: (c: 'professional' | 'celebrity') => void;
  tag: string;
  setTag: (t: string) => void;
  instantiating: string | null;
  onInstantiate: (slug: string) => void;
  onClose: () => void;
  err: string | null;
}) {
  // Filter templates by category and (optional) tag.
  const filtered = (templates || []).filter(t =>
    t.category === category && (!tag || t.tags.includes(tag))
  );
  // Collect distinct tags for the active category for the chip filter.
  const allTagsForCat = Array.from(new Set(
    (templates || [])
      .filter(t => t.category === category)
      .flatMap(t => t.tags)
  ));

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 200,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '90vw', maxWidth: 920, height: '85vh', maxHeight: 720,
          background: 'var(--bg-0)', border: '1px solid var(--line-2)',
          borderRadius: 8, display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div className="flex between center" style={{
          padding: '14px 18px', borderBottom: '1px solid var(--line-1)',
        }}>
          <div className="col gap-1">
            <div style={{ fontSize: 16, fontWeight: 500 }}>📚 专家库</div>
            <div className="meta" style={{ fontSize: 11 }}>
              一键拉起 — 加入会议后可任意修改 persona / 模型 / 立场
            </div>
          </div>
          <button className="btn ghost" onClick={onClose}>{I.x} 关闭</button>
        </div>

        {/* Category tabs */}
        <div className="flex" style={{ borderBottom: '1px solid var(--line-1)' }}>
          {(['professional', 'celebrity'] as const).map(c => (
            <button
              key={c}
              onClick={() => { setCategory(c); setTag(''); }}
              style={{
                flex: 1, padding: '12px 16px', border: 'none',
                background: category === c ? 'var(--bg-2)' : 'transparent',
                color: category === c ? 'var(--fg-0)' : 'var(--fg-2)',
                fontFamily: 'var(--mono)', fontSize: 12, cursor: 'pointer',
                borderBottom: category === c ? '2px solid var(--live)' : '2px solid transparent',
              }}
            >
              {c === 'professional' ? '专业 · Professional' : '名人 · Celebrity'}
            </button>
          ))}
        </div>

        {/* Tag chips */}
        {allTagsForCat.length > 0 && (
          <div className="flex" style={{
            padding: '10px 18px', gap: 6, flexWrap: 'wrap',
            borderBottom: '1px solid var(--line-1)',
          }}>
            <button
              onClick={() => setTag('')}
              style={{
                padding: '3px 10px', borderRadius: 100,
                fontFamily: 'var(--mono)', fontSize: 10,
                background: !tag ? 'var(--live)' : 'transparent',
                color: !tag ? 'var(--bg-0)' : 'var(--fg-2)',
                border: '1px solid ' + (!tag ? 'var(--live)' : 'var(--line-2)'),
                cursor: 'pointer',
              }}
            >
              全部
            </button>
            {allTagsForCat.map(tg => (
              <button
                key={tg}
                onClick={() => setTag(tg)}
                style={{
                  padding: '3px 10px', borderRadius: 100,
                  fontFamily: 'var(--mono)', fontSize: 10,
                  background: tag === tg ? 'var(--live)' : 'transparent',
                  color: tag === tg ? 'var(--bg-0)' : 'var(--fg-2)',
                  border: '1px solid ' + (tag === tg ? 'var(--live)' : 'var(--line-2)'),
                  cursor: 'pointer',
                }}
              >
                {tg}
              </button>
            ))}
          </div>
        )}

        {/* Body grid */}
        <div className="grow scroll" style={{ padding: 16 }}>
          {err && (
            <div className="mono tiny" style={{
              padding: '8px 10px', marginBottom: 10,
              border: '1px solid var(--err)', borderRadius: 4, color: 'var(--err)',
            }}>! {err}</div>
          )}
          {!templates ? (
            <div className="meta dim">loading…</div>
          ) : filtered.length === 0 ? (
            <div className="meta dim">没有匹配的专家</div>
          ) : (
            <div style={{
              display: 'grid', gap: 10,
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            }}>
              {filtered.map(t => (
                <TemplateCard
                  key={t.slug}
                  template={t}
                  busy={instantiating === t.slug}
                  onPick={() => onInstantiate(t.slug)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer attribution */}
        <div className="meta dim" style={{
          padding: '8px 18px', borderTop: '1px solid var(--line-1)',
          fontSize: 10, lineHeight: 1.5,
        }}>
          专业角色基于 awesome-chatgpt-prompts (CC0) / awesome-chatgpt-prompts-zh (MIT) ·
          名人角色为公开方法论改写的"风格借鉴",非本人 ·
          schema 来自 LangGPT (Apache-2.0)
        </div>
      </div>
    </div>
  );
}

function TemplateCard({ template: t, busy, onPick }: {
  template: AgentTemplate;
  busy: boolean;
  onPick: () => void;
}) {
  const accent = t.category === 'celebrity' ? 'var(--judge)' : 'var(--live)';
  return (
    <div style={{
      border: '1px solid var(--line-2)', borderRadius: 6,
      padding: 12, background: 'var(--bg-1)',
      display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div className="flex center gap-2">
        <div style={{
          width: 32, height: 32, borderRadius: '50%',
          background: accent, color: 'var(--bg-0)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 600,
          flexShrink: 0,
        }}>{t.short}</div>
        <div className="col gap-1 grow" style={{ minWidth: 0 }}>
          <div style={{
            fontSize: 13, fontWeight: 500,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>{t.name}</div>
          <div className="meta dim" style={{ fontSize: 10 }}>{t.stance_label || ''}</div>
        </div>
      </div>
      <div style={{
        fontSize: 11, color: 'var(--fg-1)', lineHeight: 1.5,
        minHeight: 32,
      }}>{t.one_liner}</div>
      <div className="flex" style={{ gap: 4, flexWrap: 'wrap' }}>
        {t.tags.slice(0, 3).map(tg => (
          <span key={tg} className="mono tiny" style={{
            padding: '2px 6px', border: '1px solid var(--line-2)',
            borderRadius: 100, fontSize: 9, color: 'var(--fg-2)',
          }}>{tg}</span>
        ))}
      </div>
      <button
        className="btn primary"
        disabled={busy}
        onClick={onPick}
        style={{ width: '100%', fontSize: 11 }}
      >
        {busy ? '$ 加入中…' : '+ 加入'}
      </button>
    </div>
  );
}

function PackEditor({ pack, isNew, onSave, onDelete, onCancel }) {
  const [name, setName] = React.useState(pack.name || '');
  const [agentIds, setAgentIds] = React.useState(pack.agentIds || []);

  const toggle = (id) => {
    setAgentIds(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  };

  return (
    <div onClick={onCancel} style={{
      position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: 480, background: 'var(--bg-1)', border: '1px solid var(--line-2)',
        borderRadius: 6, boxShadow: '0 18px 60px rgba(0,0,0,0.45)',
      }}>
        <div className="flex between center" style={{
          padding: '12px 16px', borderBottom: '1px solid var(--line-1)',
        }}>
          <div className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>
            {isNew ? 'NEW STARTER PACK' : 'EDIT STARTER PACK'}
          </div>
          <button className="btn ghost icon" onClick={onCancel}>{I.x}</button>
        </div>
        <div className="col gap-4" style={{ padding: 18 }}>
          <div className="col gap-2">
            <div className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>NAME</div>
            <input className="input" autoFocus value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. 产品评审 · 4 人" />
          </div>
          <div className="col gap-2">
            <div className="flex between center">
              <div className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>
                AGENTS · {agentIds.length} selected
              </div>
              <span className="meta">click to toggle</span>
            </div>
            <div className="col gap-1" style={{
              maxHeight: 260, overflow: 'auto', border: '1px solid var(--line-1)', borderRadius: 4,
            }}>
              {AGENTS.filter(a => a.id !== 'U1').map(a => {
                const on = agentIds.includes(a.id);
                return (
                  <div key={a.id} onClick={() => toggle(a.id)} className="flex center gap-2 hov"
                    style={{
                      padding: '7px 10px', cursor: 'pointer',
                      background: on ? 'var(--bg-3)' : 'transparent',
                      borderLeft: '2px solid ' + (on ? 'var(--live)' : 'transparent'),
                    }}>
                    <span className="mono tiny dim" style={{ width: 16 }}>{on ? '✓' : ' '}</span>
                    <Avatar agent={a} size="sm" />
                    <div className="col grow" style={{ minWidth: 0 }}>
                      <div className="sm">{a.name}</div>
                      <div className="flex center gap-2"><StanceTag agent={a} /><ModelChip modelId={a.model} /></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="flex between center" style={{
          padding: '12px 16px', borderTop: '1px solid var(--line-1)',
          background: 'var(--bg-2)',
        }}>
          {!isNew ? (
            <button className="btn ghost" onClick={onDelete} style={{ color: 'var(--err)' }}>
              delete pack
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <button className="btn ghost" onClick={onCancel}>cancel</button>
            <button className="btn primary"
              disabled={!name.trim() || agentIds.length === 0}
              style={{
                opacity: (!name.trim() || agentIds.length === 0) ? 0.5 : 1,
                cursor: (!name.trim() || agentIds.length === 0) ? 'default' : 'pointer',
              }}
              onClick={() => name.trim() && agentIds.length && onSave({ name: name.trim(), agentIds })}>
              {isNew ? 'create' : 'save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StudioForm({
  agent,
  onChange,
  onSave,
  onDuplicate,
  onDelete,
  saving,
  error,
  dirty,
}: {
  agent: UIAgent;
  onChange: (patch: Partial<UIAgent>) => void;
  onSave: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  saving: boolean;
  error: string | null;
  dirty: boolean;
}) {
  const { t } = useI18n();
  // Local-only ephemeral controls (not persisted yet).
  const [temp, setTemp] = React.useState(0.4);
  const [maxTok, setMaxTok] = React.useState(400);
  React.useEffect(() => { setTemp(0.4); setMaxTok(400); }, [agent.id]);

  const stance = agent.stance;
  const model = agent.modelLocal;
  const c = STANCE_COLOR[stance] || 'var(--fg-2)';

  const setStance = (id: string) => {
    const opt = STANCE_OPTS.find(o => o.id === id);
    onChange({ stance: id, stance_label: opt ? opt.stanceLabel : id });
  };
  const setModel = (m: LocalModelId) => onChange({ modelLocal: m });

  return (
    <div className="col gap-5" style={{ padding: '20px 28px 32px', maxWidth: 720 }}>
      {/* header */}
      <div className="flex center gap-3">
        <div style={{
          width: 48, height: 48, borderRadius: 6,
          background: c, color: 'var(--bg-0)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'var(--mono)', fontSize: 16, fontWeight: 600,
        }}>{agent.short}</div>
        <div className="col grow">
          <div className="mono tiny dim">
            agent · {agent.id}
            {dirty && <span style={{ marginLeft: 8, color: 'var(--warn)' }}>· unsaved changes</span>}
          </div>
          <input value={agent.name}
            onChange={(e) => onChange({ name: e.target.value })}
            style={{
              fontSize: 22, fontWeight: 500, letterSpacing: '-0.01em',
              background: 'transparent', border: 0, outline: 'none', color: 'var(--fg-0)',
              padding: 0, fontFamily: 'inherit',
            }} />
          <div className="meta">
            stance: {agent.stance_label || agent.stance} · short: {agent.short}
          </div>
        </div>
        <button className="btn ghost" onClick={onDuplicate} disabled={saving || agent.isNew}>
          {t('app.studio.duplicate')}
        </button>
        <button className="btn ghost" onClick={onDelete} disabled={saving}
          style={{ color: 'var(--err)' }}>
          delete
        </button>
        <button className="btn" onClick={onSave} disabled={saving}>
          {saving ? 'saving…' : t('app.studio.save')}
        </button>
      </div>

      {error && (
        <div className="mono tiny" style={{
          padding: '8px 10px', border: '1px solid var(--err)', borderRadius: 3,
          color: 'var(--err)', background: 'rgba(220,38,38,0.06)',
        }}>
          {error}
        </div>
      )}

      <div className="hr" />

      {/* short code */}
      <Field label="SHORT · 2-letter avatar code" hint="2 个字符,显示在 avatar 圆形中。">
        <input className="input mono" value={agent.short} maxLength={4}
          onChange={(e) => onChange({ short: e.target.value.toUpperCase().slice(0, 4) })}
          style={{ width: 100 }} />
      </Field>

      {/* persona */}
      <Field label={t('app.studio.persona') + ' · system prompt'} hint="决定 agent 的人格、视角、表达风格。会被注入到每次调用的 system 段。">
        <textarea className="input mono" rows={6}
          value={agent.persona}
          onChange={(e) => onChange({ persona: e.target.value })}
          placeholder="describe this agent's role, point of view, tone, and constraints…" />
      </Field>

      {/* two-col: stance + model */}
      <div className="flex gap-4">
        <Field label={t('app.studio.stance') + ' · 立场标签'} sub="决定调度策略中如何分组" style={{ flex: 1 }}>
          <SegRadio
            value={stance}
            onChange={setStance}
            options={STANCE_OPTS.map(o => ({ id: o.id, label: o.label, color: o.color }))}
          />
        </Field>
      </div>

      <Field label={t('app.studio.model') + ' · LLM provider'} hint="支持混搭 — 不同 agent 可用不同模型。openllm.dev 命名空间。">
        <div className="flex gap-2 wrap">
          {(Object.entries(MODELS) as [LocalModelId, typeof MODELS[LocalModelId]][]).map(([id, m]) => (
            <div key={id}
              onClick={() => setModel(id)}
              className="flex center gap-2"
              style={{
                padding: '8px 12px',
                border: '1px solid ' + (model === id ? 'var(--live)' : 'var(--line-2)'),
                background: model === id ? 'var(--bg-3)' : 'var(--bg-2)',
                borderRadius: 4, cursor: 'pointer',
                boxShadow: model === id ? '0 0 0 1px var(--live) inset' : 'none',
              }}>
              <ModelGlyph id={id} size={12} />
              <div className="col">
                <span className="sm mono">{m.name}</span>
                <span className="meta">{m.vendor}</span>
              </div>
            </div>
          ))}
        </div>
      </Field>

      <div className="flex gap-4">
        <Field label={t('app.studio.temperature')} sub="0.0 严谨 · 1.0 发散" style={{ flex: 1 }}>
          <RangeRow value={temp} onChange={setTemp} min={0} max={1} step={0.05} />
        </Field>
        <Field label={t('app.studio.maxTokens')} sub="单次发言上限" style={{ flex: 1 }}>
          <RangeRow value={maxTok} onChange={setMaxTok} min={100} max={2000} step={50} unit=" tok" />
        </Field>
      </div>

      <KnowledgeSection />

      <Field label={t('app.studio.tools') + ' · 工具调用'}>
        <div className="col gap-1">
          {[
            { id: 'calc', name: 'calculator',     desc: '基础数值计算', on: true },
            { id: 'web',  name: 'web_search',     desc: '搜索市场公开数据', on: true },
            { id: 'sql',  name: 'query_warehouse', desc: '只读访问财务数仓', on: true },
            { id: 'vote', name: 'cast_vote',      desc: '在投票轮次中提交选项', on: true },
            { id: 'doc',  name: 'edit_minutes',   desc: '直接修改会议纪要', on: false },
          ].map(t => (
            <div key={t.id} className="flex center gap-3" style={{ padding: '6px 0', borderBottom: '1px solid var(--line-1)' }}>
              <span className="mono sm" style={{ width: 130, color: t.on ? 'var(--fg-0)' : 'var(--fg-3)' }}>
                {t.name}
              </span>
              <span className="meta grow">{t.desc}</span>
              <Toggle on={t.on} />
            </div>
          ))}
        </div>
      </Field>
    </div>
  );
}

function KnowledgeSection() {
  const [files, setFiles] = React.useState<any[]>([]);
  const [available, setAvailable] = React.useState<boolean | null>(null);
  const [err, setErr] = React.useState<string | null>(null);
  const [uploading, setUploading] = React.useState(false);
  const [picked, setPicked] = React.useState<File | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const refresh = React.useCallback(async () => {
    try {
      const { files } = await knowledgeApi.list();
      setFiles(files || []);
      setAvailable(true);
      setErr(null);
    } catch (e) {
      if (e instanceof HTTPError && e.status === 404) {
        setAvailable(false);
        setErr(null);
      } else {
        setAvailable(true);
        setErr(e instanceof HTTPError
          ? `knowledge.list failed (HTTP ${e.status})`
          : (e as Error).message || 'knowledge.list failed');
      }
    }
  }, []);

  React.useEffect(() => { refresh(); }, [refresh]);

  const onUpload = async () => {
    if (!picked) return;
    setUploading(true);
    setErr(null);
    try {
      await knowledgeApi.upload(picked);
      setPicked(null);
      if (inputRef.current) inputRef.current.value = '';
      await refresh();
    } catch (e) {
      if (e instanceof HTTPError && e.status === 404) {
        setAvailable(false);
      } else {
        setErr(e instanceof HTTPError
          ? `upload failed (HTTP ${e.status})`
          : (e as Error).message || 'upload failed');
      }
    } finally {
      setUploading(false);
    }
  };

  const onRemove = async (id: string) => {
    setErr(null);
    try {
      await knowledgeApi.remove(id);
      await refresh();
    } catch (e) {
      if (e instanceof HTTPError && e.status === 404) {
        setAvailable(false);
      } else {
        setErr(e instanceof HTTPError
          ? `delete failed (HTTP ${e.status})`
          : (e as Error).message || 'delete failed');
      }
    }
  };

  return (
    <Field label="KNOWLEDGE · 私有上下文" hint="附加文档、上次会议纪要、专属事实。每次调用作为参考嵌入。">
      {available === false && (
        <div className="mono tiny" style={{
          padding: '8px 10px', border: '1px dashed var(--line-2)', borderRadius: 4,
          color: 'var(--fg-2)', background: 'var(--bg-2)',
        }}>
          Knowledge uploads · coming soon (Wave 5)
        </div>
      )}
      {available === true && (
        <div className="col gap-2">
          {err && (
            <div className="mono tiny" style={{
              padding: '6px 8px', border: '1px solid var(--err)', borderRadius: 3,
              color: 'var(--err)', background: 'rgba(220,38,38,0.06)',
            }}>{err}</div>
          )}
          {files.length === 0 && !err && (
            <div className="meta">no files attached yet</div>
          )}
          {files.map((f: any) => {
            const id = f.id || f.ID || f.file_id || '';
            const name = f.name || f.filename || f.original_name || '(unnamed)';
            const size = f.size != null
              ? (f.size > 1024 ? `${Math.round(f.size / 1024)} kb` : `${f.size} b`)
              : '';
            const tag = (name.split('.').pop() || 'FILE').toUpperCase();
            return (
              <div key={id || name} className="flex center gap-3 mono sm" style={{
                padding: '7px 10px', border: '1px solid var(--line-1)', borderRadius: 4,
              }}>
                <span className="tag" style={{ fontSize: 9 }}>{tag}</span>
                <span className="grow">{name}</span>
                {size && <span className="dim tiny">{size}</span>}
                <button className="btn ghost icon" onClick={() => onRemove(id)} disabled={!id}>
                  {I.x}
                </button>
              </div>
            );
          })}
          <div className="flex center gap-2">
            <input
              ref={inputRef}
              type="file"
              onChange={(e) => setPicked(e.target.files && e.target.files[0] || null)}
              style={{ fontFamily: 'var(--mono)', fontSize: 11 }}
            />
            <button className="btn ghost" onClick={onUpload} disabled={!picked || uploading}>
              {uploading ? 'uploading…' : (<>{I.plus} upload</>)}
            </button>
          </div>
        </div>
      )}
      {available === null && (
        <div className="meta">loading…</div>
      )}
    </Field>
  );
}

function Field({ label, sub, hint, children, style }: {
  label: string;
  sub?: string;
  hint?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <div className="col gap-2" style={style}>
      <div>
        <div className="mono tiny dim" style={{ letterSpacing: '0.08em', color: 'var(--fg-2)' }}>
          {label}
          {sub && <span className="dim" style={{ marginLeft: 8, textTransform: 'none', letterSpacing: 0 }}>· {sub}</span>}
        </div>
        {hint && <div className="meta" style={{ marginTop: 3 }}>{hint}</div>}
      </div>
      {children}
    </div>
  );
}

function SegRadio({ value, options, onChange }) {
  return (
    <div className="flex" style={{ border: '1px solid var(--line-2)', borderRadius: 4, overflow: 'hidden' }}>
      {options.map(o => {
        const active = o.id === value;
        return (
          <div key={o.id}
            onClick={() => onChange && onChange(o.id)}
            style={{
              flex: 1, padding: '8px 10px',
              background: active ? 'var(--bg-3)' : 'var(--bg-2)',
              borderRight: '1px solid var(--line-1)',
              cursor: 'pointer',
              color: active ? o.color : 'var(--fg-2)',
              fontFamily: 'var(--mono)', fontSize: 11,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              fontWeight: active ? 600 : 400,
            }}>
            <span className="dot" style={{ background: o.color, opacity: active ? 1 : 0.5 }} />
            {o.label}
          </div>
        );
      })}
    </div>
  );
}

function RangeRow({ value, onChange, min, max, step, unit = '' }) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="flex center gap-3">
      <div style={{ flex: 1, position: 'relative', height: 18 }}>
        <div style={{
          position: 'absolute', top: 6, left: 0, right: 0, height: 6,
          background: 'var(--bg-3)', borderRadius: 1,
        }} />
        <div style={{
          position: 'absolute', top: 6, left: 0, width: pct + '%', height: 6,
          background: 'var(--live)', borderRadius: 1, pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', top: 3, left: `calc(${pct}% - 6px)`,
          width: 12, height: 12, borderRadius: 2, background: 'var(--fg-0)',
          pointerEvents: 'none',
        }} />
        <input type="range" min={min} max={max} step={step} value={value}
          onChange={(e) => onChange && onChange(parseFloat(e.target.value))}
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            opacity: 0, cursor: 'pointer', margin: 0,
          }} />
      </div>
      <span className="mono sm tnum" style={{ width: 70, textAlign: 'right' }}>
        {Number.isInteger(value) ? value : value.toFixed(2)}{unit}
      </span>
    </div>
  );
}

function Toggle({ on }) {
  return (
    <div style={{
      width: 30, height: 16, borderRadius: 8,
      background: on ? 'var(--live)' : 'var(--bg-3)',
      border: '1px solid ' + (on ? 'var(--live)' : 'var(--line-2)'),
      position: 'relative', cursor: 'pointer',
    }}>
      <div style={{
        position: 'absolute', top: 1, left: on ? 15 : 1,
        width: 12, height: 12, borderRadius: '50%',
        background: on ? '#052e16' : 'var(--fg-2)',
        transition: 'left .12s',
      }} />
    </div>
  );
}

function StudioPreview({ agent }: { agent: UIAgent }) {
  // Show a small "if you asked them to comment on a draft motion" preview.
  // Real provider call lives in a future wave — for now, this is a canned
  // streamed sample so the UI keeps showing motion.
  const previewMsg = {
    id: 'preview-' + agent.id,
    streaming: true,
    chunks: [
      '基于财务模型,', ' 我对这个 pilot 的支持是有条件的:',
      ' 第 4 个月新签 ARR ≥ $80k,', ' CAC payback < 14 个月,',
      ' 否则强制 sunset。',
    ],
  };
  const { text, done } = useStreamedText(previewMsg, { msPerChunk: 380 });
  const shim = uiToShim(agent);
  return (
    <div className="col grow gap-3" style={{ padding: 14 }}>
      <div className="mono tiny" style={{
        padding: '6px 8px', border: '1px dashed var(--line-2)', borderRadius: 3,
        color: 'var(--fg-2)', background: 'var(--bg-2)',
      }}>
        Dry run via real provider — coming next. The output below is a canned sample.
      </div>

      <div className="col gap-1" style={{ padding: 10, border: '1px dashed var(--line-2)', borderRadius: 4 }}>
        <div className="mono tiny dim">PROMPT</div>
        <div className="sm" style={{ fontFamily: 'var(--mono)', color: 'var(--fg-1)' }}>
          针对动议 "Q3 进入巴西, $1.2M / 9 个月 pilot",发表 80 字以内立场。
        </div>
      </div>

      <div className="col gap-2" style={{
        padding: 12, background: 'var(--bg-2)', border: '1px solid var(--line-2)', borderRadius: 4,
      }}>
        <div className="flex center gap-2">
          <Avatar agent={shim} size="sm" />
          <span className="sm" style={{ fontWeight: 500 }}>{agent.name}</span>
          {agent.modelLocal && <ModelChip modelId={agent.modelLocal} />}
          <span className="dim mono tiny" style={{ marginLeft: 'auto' }}>~ 1.4s · 32 tok</span>
        </div>
        <div className="sm" style={{ lineHeight: 1.55, color: 'var(--fg-0)' }}>
          {renderInline(text)}{!done && <span className="caret" />}
        </div>
        <div className="flex center gap-2 mono tiny dim" style={{ paddingTop: 6, borderTop: '1px solid var(--line-1)' }}>
          <span>tokens 187 in / 64 out</span>
          <span>·</span>
          <span>cost $0.04</span>
          <span>·</span>
          <span>temp 0.4</span>
        </div>
      </div>

      <div className="grow" />

      <button className="btn primary" disabled style={{ width: '100%', opacity: 0.5 }}>
        {I.play} run another sample
      </button>
      <button className="btn ghost" style={{ width: '100%' }}>add to current meeting</button>
    </div>
  );
}

